import api from './api';
import {uploadFilesSequential} from '@/utils/fileUploadQueue';

const COMMIT_RETRYABLE_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

const DEFAULT_COUNTS = {
    total: 0,
    active: 0,
    annulled: 0,
    late: 0,
    no_photo: 0,
    system_photo_issue: 0,
    no_fuel: 0,
    no_telegram: 0,
    problem: 0
};

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function isCommitRetriableError(error) {
    if (!error) return false;
    const response = error.response;
    if (!response) return true;
    const status = response.status;
    return COMMIT_RETRYABLE_HTTP_STATUSES.has(status) || status >= 500;
}

function buildCounts(items, total, serverCounts) {
    const counts = (items || []).reduce((acc, item) => {
        const flags = item && item.flags ? item.flags : {};
        if (flags.late) acc.late += 1;
        if (flags.no_photo) acc.no_photo += 1;
        if (flags.system_photo_issue) acc.system_photo_issue += 1;
        if (flags.no_fuel) acc.no_fuel += 1;
        if (flags.no_telegram_message) acc.no_telegram += 1;
        if (flags.problem) acc.problem += 1;
        return acc;
    }, {...DEFAULT_COUNTS, total: total || 0});
    return {
        ...counts,
        active: Number(serverCounts && serverCounts.active) || 0,
        annulled: Number(serverCounts && serverCounts.annulled) || 0
    };
}

export default {
    namespaced: true,
    state: {
        items: [],
        loading: false,
        total: 0,
        page: 1,
        perPage: 25,
        sortBy: 'date_time_begin',
        sortDesc: true,
        query: '',
        reportStatus: 'all',
        objectStatus: 'active',
        issue: 'all',
        buildingProcessId: 'all',
        authorId: 'all',
        dateFrom: '',
        dateTo: '',
        filterOptions: {
            building_processes: [],
            authors: []
        },
        counts: {...DEFAULT_COUNTS}
    },
    getters: {
        listParams(state) {
            const params = {
                page: state.page,
                per_page: state.perPage,
                sort_by: state.sortBy,
                sort_desc: String(state.sortDesc),
                object_status: state.objectStatus || 'active'
            };
            if (state.reportStatus && state.reportStatus !== 'all') {
                params.report_status = state.reportStatus;
            }
            if (state.query) params.q = state.query;
            if (state.issue && state.issue !== 'all') params.issue = state.issue;
            if (state.buildingProcessId && state.buildingProcessId !== 'all') {
                params.building_process_id = state.buildingProcessId;
            }
            if (state.authorId && state.authorId !== 'all') params.author_id = state.authorId;
            if (state.dateFrom) params.date_from = state.dateFrom;
            if (state.dateTo) params.date_to = state.dateTo;
            return params;
        }
    },
    mutations: {
        setItems(state, items) {
            state.items = items || [];
        },
        setLoading(state, value) {
            state.loading = value;
        },
        setTotal(state, value) {
            state.total = Number(value) || 0;
        },
        setPage(state, value) {
            state.page = Number(value) || 1;
        },
        setPerPage(state, value) {
            state.perPage = Number(value) || 25;
        },
        setSortBy(state, value) {
            state.sortBy = value || 'date_time_begin';
        },
        setSortDesc(state, value) {
            state.sortDesc = Boolean(value);
        },
        setQuery(state, value) {
            state.query = value || '';
        },
        setReportStatus(state, value) {
            state.reportStatus = ['active', 'annulled'].indexOf(value) !== -1 ? value : 'all';
        },
        setObjectStatus(state, value) {
            state.objectStatus = value === 'archive' ? 'archive' : 'active';
        },
        setIssue(state, value) {
            state.issue = value || 'all';
        },
        setBuildingProcessId(state, value) {
            state.buildingProcessId = value || 'all';
        },
        setAuthorId(state, value) {
            state.authorId = value || 'all';
        },
        setDateFrom(state, value) {
            state.dateFrom = value || '';
        },
        setDateTo(state, value) {
            state.dateTo = value || '';
        },
        setFilterOptions(state, value) {
            state.filterOptions = value || {building_processes: [], authors: []};
        },
        setCounts(state, value) {
            state.counts = {...DEFAULT_COUNTS, ...(value || {})};
        },
        resetFilters(state) {
            state.query = '';
            state.reportStatus = 'all';
            state.objectStatus = 'active';
            state.issue = 'all';
            state.buildingProcessId = 'all';
            state.authorId = 'all';
            state.dateFrom = '';
            state.dateTo = '';
            state.page = 1;
        }
    },
    actions: {
        async fetchReports({commit, getters, state}, options = {}) {
            if (options.resetPage) {
                commit('setPage', 1);
            }
            if (options.page) {
                commit('setPage', options.page);
            }
            commit('setLoading', true);
            try {
                const data = await api.getReports(getters.listParams);
                const items = data && Array.isArray(data.items) ? data.items : [];
                commit('setItems', items);
                commit('setTotal', data && data.total != null ? data.total : 0);
                commit('setPage', data && data.page ? data.page : state.page);
                commit('setPerPage', data && data.per_page ? data.per_page : state.perPage);
                commit('setCounts', buildCounts(items, data && data.total, data && data.counts));
                return data;
            } finally {
                commit('setLoading', false);
            }
        },
        async fetchFilterOptions({commit, state}) {
            const data = await api.getFilterOptions({
                object_status: state.objectStatus || 'active'
            });
            commit('setFilterOptions', data);
            return data;
        },
        fetchAccessSettings() {
            return api.getAccessSettings();
        },
        updateAccessSettings(_, accessConfig) {
            return api.updateAccessSettings(accessConfig);
        },
        fetchObjectAccess(_, params = {}) {
            return api.getObjectAccess(params);
        },
        updateObjectAccess(_, {id, accessConfig}) {
            return api.updateObjectAccess(id, accessConfig);
        },
        fetchReport(_, id) {
            return api.getReport(id);
        },
        fetchActivity(_, id) {
            return api.getActivity(id);
        },
        fetchMediaPreview(_, {id, limit}) {
            return api.getMediaPreview(id, {limit});
        },
        async commitTempPhotos({commit}, {id, photos, tempIds}) {
            const list = Array.isArray(tempIds)
                ? tempIds
                : (Array.isArray(photos) ? photos.map(item => item && item.id) : []);
            const ids = Array.from(new Set(list.map(item => Number(item)).filter(Boolean)));
            if (!id || !ids.length) {
                return null;
            }

            const total = ids.length;
            commit('shiftReport_setUploadProgress', {done: 0, total}, {root: true});

            try {
                let remainingIds = ids;
                let attachedIds = [];

                const commitWithRetry = async idsToCommit => {
                    const maxAttempts = 3;
                    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                        try {
                            return await api.commitTempPhotos(id, idsToCommit);
                        } catch (error) {
                            if (attempt >= maxAttempts || !isCommitRetriableError(error)) {
                                throw error;
                            }
                            await sleep(400 * Math.pow(2, attempt - 1));
                        }
                    }
                    return null;
                };

                for (let pass = 1; pass <= 3 && remainingIds.length; pass += 1) {
                    const response = await commitWithRetry(remainingIds);
                    const attached = Array.isArray(response && response.ids) ? response.ids : [];
                    const failed = Array.isArray(response && response.failedIds) ? response.failedIds : [];

                    attached.forEach(attachedId => attachedIds.push(attachedId));
                    attachedIds = Array.from(new Set(attachedIds));
                    remainingIds = Array.from(new Set(failed));

                    commit('shiftReport_setUploadProgress', {
                        done: Math.min(total, attachedIds.length),
                        total
                    }, {root: true});
                }

                if (remainingIds.length) {
                    const wrappedPartial = new Error(
                        `Часть фотографий не удалось привязать автоматически: ${remainingIds.length} из ${total}`
                    );
                    wrappedPartial.code = 'PHOTO_ATTACH_PARTIAL';
                    wrappedPartial.payload = {
                        total,
                        attachedIds,
                        failedIds: remainingIds
                    };
                    throw wrappedPartial;
                }

                commit('shiftReport_setUploadProgress', {done: total, total}, {root: true});
                return {attachedCount: attachedIds.length, ids: attachedIds};
            } catch (error) {
                const wrapped = new Error(error && error.message ? error.message : 'Ошибка при привязке фотографий');
                wrapped.code = 'PHOTO_ATTACH_FAILED';
                wrapped.payload = error && error.payload ? error.payload : null;
                throw wrapped;
            }
        },
        resendPhotos(_, id) {
            return api.resendPhotos(id);
        },
        resendTelegramReport(_, id) {
            return api.resendTelegramReport(id);
        },
        addComment(_, {id, text}) {
            return api.addComment(id, text);
        },
        editComment(_, {id, commentId, text}) {
            return api.editComment(id, commentId, text);
        },
        deleteComment(_, {id, commentId}) {
            return api.deleteComment(id, commentId);
        },
        uploadCommentFiles(_, {id, commentId, files}) {
            return uploadFilesSequential(
                files,
                file => api.uploadCommentFiles(id, commentId, [file])
            );
        },
        deleteCommentFile(_, {id, commentId, fileId}) {
            return api.deleteCommentFile(id, commentId, fileId);
        }
    }
};
